import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { TenantService } from './tenant.service';
import {
  BillingStatus, blankStatus, readStatus, isSettled, readForbidden,
} from '../utils/billing-restriction.utils';

/**
 * v1.6.1538 — billing restriction state (Asana 1218431462710845).
 *
 * Holds the current verdict for the whole app: the interceptor writes to it
 * from any 403, the notice renders from it, and the store switcher re-fetches
 * through it.
 *
 * Both endpoints stay reachable while restricted — along with login, logout,
 * `/tenant/info` and `/tenant/profile` — so they must never be gated by the
 * interceptor that gates everything else.
 */
@Injectable({ providedIn: 'root' })
export class BillingStatusService {
  private get base(): string { return `${this.tenant.apiBaseUrl}/v1/tenant/billing`; }

  /** The paths that keep working while restricted; see section 4 of the spec. */
  static readonly NEVER_GATED = [
    '/v1/tenant/billing/status',
    '/v1/tenant/billing/pay-now',
    '/v1/tenant/info',
    '/v1/tenant/profile',
    '/login',
    '/logout',
  ];

  /**
   * v1.6.1572 — why the last read failed, or null when it succeeded.
   *
   * `forbidden` means the staff member cannot see billing — expected, and
   * silent. Anything else means we DO NOT KNOW the billing state, which is a
   * different thing from knowing there is nothing to pay.
   */
  private readonly _lastError$ =
    new BehaviorSubject<{ kind: 'forbidden' | 'failed'; message: string } | null>(null);
  readonly lastError$ = this._lastError$.asObservable();
  get lastError() { return this._lastError$.value; }

  private readonly _status$ = new BehaviorSubject<BillingStatus>(blankStatus());
  readonly status$ = this._status$.asObservable();
  get snapshot(): BillingStatus { return this._status$.value; }

  constructor(private http: HttpClient, private tenant: TenantService) {}

  /**
   * `X-Store-Id` selects the store context. Sent only when a store is actually
   * selected: without it the verdict is for the tenant as a whole, and sending
   * a stale id would report the wrong scope after a switch.
   */
  fetch(storeId: number | null): Observable<BillingStatus> {
    return this.read(storeId).pipe(
      tap((res: any) => {
        this._lastError$.next(null);   // v1.6.1572 — we know the state again
        this._status$.next(readStatus(res));
      }),
      catchError((err: any) => {
        /**
         * v1.6.1572 — a failure must not LOCK the panel, but it must not read
         * as "nothing owed" either.
         *
         * Operator: "I have one that is past due and I don't see it." The
         * v1.6.1538 handler turned EVERY failure into a blank status, which is
         * the exact shape of a healthy tenant — so a 403, a 500 and a dropped
         * connection all rendered as silence, identical to having nothing to
         * pay. Same silent-catch mistake as the empty product picker in
         * v1.6.1564.
         *
         * The status is still blanked, so nothing locks. But WHY it is blank is
         * now recorded, so the panel can say it could not check rather than
         * implying it checked and found nothing.
         */
        const status = Number(err?.status);
        this._lastError$.next(
          status === 403
            // The one benign case: no permission to see billing at all.
            ? { kind: 'forbidden', message: '' }
            : { kind: 'failed', message: String(err?.error?.message ?? '').trim()
                || `Billing status could not be checked (HTTP ${status || 'no response'}).` });
        this._status$.next(blankStatus());
        return of(blankStatus());
      }),
      // The observable yields the parsed status either way.
      tap(() => {}),
    ) as Observable<BillingStatus>;
  }

  /**
   * Asana 1220202714832247 — the RAW status read, errors NOT swallowed.
   *
   * `fetch()` turns every failure into a blank (= allowed) status so the panel
   * never locks itself out. That is right for the notice and WRONG for the
   * payment result page, where "allowed" means "Restored": a 500 or a dropped
   * connection must not be announced as a restored account, and a 401 has to
   * reach the page so it can switch to its signed-out view.
   *
   * Same request as `fetch()` (it is built on this), so `X-Store-Id` is sent
   * the same way.
   */
  read(storeId: number | null): Observable<any> {
    let headers = new HttpHeaders();
    if (storeId != null) headers = headers.set('X-Store-Id', String(storeId));
    return this.http.get(`${this.base}/status`, { headers });
  }

  /** Adopt the verdict carried on a billing 403, so a mid-session block paints. */
  adoptFromForbidden(err: any): void {
    const partial = readForbidden(err);
    this._status$.next({ ...blankStatus(), ...partial, state: 'restricted' } as BillingStatus);
  }

  /** True once the balance is actually zero — a PARTIAL payment is not enough. */
  get settled(): boolean { return isSettled(this.snapshot); }

  /**
   * Opens the payment page for the ACCOUNT OWNER'S whole balance, whoever
   * clicked and whichever store is selected. The url is never cached.
   */
  payNow(): Observable<any> {
    return this.http.post(`${this.base}/pay-now`, {});
  }
}
